/*  iceberg-ui  */
import { Box, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Avatar, AvatarGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { LightTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';

/* Images */
import { ReactComponent as GoogleAdminLogo } from 'images/Google_Admin.svg';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

// Microsoft, Windows, and Google keep the FontSizes[1] because they actually fill the box in which they are placed
// and look bigger than the others.
const getConfig = (theme) => ({
    azure: {
        icon: (
            <IconWithVariant
                data-testid="Windows"
                variant="avatar"
                src="Windows"
                size={FontSizes[1]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Common.microsoftEntraID.loginData'
    },
    desktop: {
        icon: (
            <IconWithVariant
                data-testid="Laptop"
                variant="avatar"
                src="Laptop"
                size={FontSizes[2]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Common.desktopData'
    },
    download: {
        icon: (
            <IconWithVariant
                data-testid="CloudDownload"
                variant="avatar"
                src="CloudDownload"
                size={FontSizes[2]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Common.downloadActivityData'
    },
    google: {
        icon: (
            <Box
                alt="Google Logo"
                data-testid="googleAdminIcon"
                sx={{
                    color: theme?.palette.icon.avatar,
                    width: '14px'
                }}
            >
                <GoogleAdminLogo />
            </Box>
        ),
        tooltip: 'Common.googleLoginData'
    },
    googleSSO: {
        icon: (
            <IconWithVariant
                data-testid="Google"
                variant="avatar"
                src="Google"
                size={FontSizes[1]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Tooltips.Badge.google_sso'
    },
    microsoftSSO: {
        icon: (
            <IconWithVariant
                data-testid="Microsoft"
                variant="avatar"
                src="Microsoft"
                size={FontSizes[1]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Tooltips.Badge.microsoft_sso'
    },
    okta: {
        icon: (
            <IconWithVariant
                data-testid="Okta"
                variant="avatar"
                src="Okta"
                size={FontSizes[1]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Common.oktaLoginData'
    },
    quickScan: {
        icon: (
            <IconWithVariant
                data-testid="Clock"
                variant="avatar"
                src="Clock"
                size={FontSizes[2]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Tooltips.Badge.quickScanLogin'
    },
    webActivity: {
        icon: (
            <IconWithVariant
                data-testid="Cloud"
                variant="avatar"
                src="Cloud"
                size={FontSizes[2]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Common.extensionData'
    },
    webLogin: {
        icon: (
            <IconWithVariant
                data-testid="VpnLock"
                variant="avatar"
                src="VpnLock"
                size={FontSizes[2]}
                wrapperProps={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            />
        ),
        tooltip: 'Common.cloudLoginData'
    }
});

const AvatarIcon = ({ showText, showValue, source, overlapAvatars, value }) => (
    <Avatar
        sx={{
            backgroundColor: 'background.avatar',
            color: 'icon.avatar',
            ...(overlapAvatars && !showText ? { marginRight: '-8px' } : {})
        }}
        data-testid="sourcesAvatarIcon"
    >
        <Box
            display="flex"
            alignItems="center"
            sx={{ paddingLeft: '4px', paddingRight: '4px' }}
        >
            {source.icon}
            {showValue && (
                <Typography ml="4px" sx={{ fontSize: FontSizes[0] }}>
                    {value}
                </Typography>
            )}
        </Box>
    </Avatar>
);

const SourceAvatar = ({ noBadge, showText, source, val }) => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="sourceAvatarWrapper"
            display="flex"
            alignItems="center"
            key={`app_source_${source}`}
            sx={{
                background: 'none',
                border: 'none',
                ...(showText ? { marginBottom: '8px' } : {})
            }}
        >
            {/* noBadge means no value shown next to the source icon */}
            {noBadge ? (
                <AvatarIcon
                    showText={showText}
                    source={source}
                    overlapAvatars
                />
            ) : (
                <Box data-testid="sourceAvatarBadge" flex={2}>
                    <AvatarIcon
                        showText={showText}
                        showValue
                        source={source}
                        value={val}
                    />
                </Box>
            )}
            {showText && (
                <Box ml="4px" data-testid="sourceAvatarTooltip" flex={10}>
                    {t(source.tooltip)}
                </Box>
            )}
        </Box>
    );
};

/**
 * Renders a group of source avatars with tooltips showing detailed information
 * @param {Object} props - Component props
 * @param {number} props.max - Maximum number of avatars to display before showing a +n overflow
 * @param {boolean} [props.noBadge] - If true, hides the value badge next to source icons
 * @param {Object} props.sources - Object containing source keys and their corresponding values
 * @param {string} [props.tooltipPlacement] - Placement of the tooltip relative to the avatar group
 * @returns {JSX.Element} Sources component with avatar group and tooltips
 */
export const Sources = ({ max, noBadge, sources, tooltipPlacement }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const config = getConfig(theme);

    if (
        !sources ||
        !Object.keys(sources).length ||
        Object.values(sources).every((item) => item === 0)
    ) {
        return <Typography>{t('Common.noData')}</Typography>;
    }

    return (
        // fit-content so that the tooltip shows at the end of the avatars, not at the end of the wide box
        <Box width="fit-content">
            <LightTooltip
                title={
                    <Box padding="8px">
                        {Object.keys(sources).map(
                            (key) =>
                                sources[key] > 0 &&
                                config[key] && (
                                    <SourceAvatar
                                        key={`tooltip_${key}`}
                                        noBadge={noBadge}
                                        showText
                                        source={config[key]}
                                        val={sources[key]}
                                    />
                                )
                        )}
                    </Box>
                }
                arrow
                placement={tooltipPlacement ?? 'right'}
                slotProps={{
                    tooltip: {
                        sx: {
                            minWidth: '400px'
                        }
                    }
                }}
            >
                <AvatarGroup
                    max={max}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap-reverse', // So that the +n shows at the end and not at the beginning
                        justifyContent: 'left'
                    }}
                    data-testid="sourcesAvatarGroup"
                >
                    {Object.keys(sources).map(
                        (key) =>
                            sources[key] > 0 &&
                            config[key] && (
                                <SourceAvatar
                                    key={`tooltip_${key}`}
                                    noBadge={noBadge}
                                    showText={false}
                                    source={config[key]}
                                    val={sources[key]}
                                />
                            )
                    )}
                </AvatarGroup>
            </LightTooltip>
        </Box>
    );
};
